import { useCallback, useState } from "react";
import { useMutation, useQuery } from "urql";
import {
  CreateReportGroupMutation,
  GenerateReportGroupReportsMutation,
  ReportGroupsQuery,
  ReportQuestionCandidatesQuery,
  UpdateReportGroupMutation,
} from "../graphql/reportGroups.graphql";
import { toUserFacingMessage } from "../lib/graphqlErrorMessage";

export interface ReportGroup {
  reportGroupId: string;
  termId: string;
  name: string;
  reportSettingJson: Record<string, unknown>;
}

export interface ReportQuestionCandidate {
  questionId: string;
  name: string;
}

/** レポート作成の結果。失敗の理由をその場で伝えるため、件数と一緒にメッセージを返す */
export interface GenerateReportsResult {
  createdCount: number | null;
  errorMessage: string | null;
}

/** 保存の結果。ダイアログを開いたまま理由を出せるよう、成否と一緒にメッセージを返す */
export interface SaveReportGroupResult {
  succeeded: boolean;
  errorMessage: string | null;
}

export interface CreateReportGroupInput {
  termId: string;
  name: string;
  reportSettingJson: Record<string, unknown>;
}

export interface UpdateReportGroupInput {
  reportGroupId: string;
  name?: string | null;
  reportSettingJson?: Record<string, unknown> | null;
}

export function useReportGroups(termId: string | null) {
  const [result, reexecute] = useQuery({
    query: ReportGroupsQuery,
    variables: { termId: termId ?? "" },
    pause: !termId,
  });

  return {
    reportGroups: result.data?.reportGroups ?? [],
    loading: result.fetching,
    error: result.error
      ? toUserFacingMessage(result.error, "取得に失敗しました")
      : null,
    refetch: useCallback(
      () => reexecute({ requestPolicy: "network-only" }),
      [reexecute],
    ),
  };
}

export function useReportQuestionCandidates(termId: string | null) {
  const [result] = useQuery({
    query: ReportQuestionCandidatesQuery,
    variables: { termId: termId ?? "" },
    pause: !termId,
  });

  return {
    reportQuestionCandidates: result.data?.reportQuestionCandidates ?? [],
    loading: result.fetching,
  };
}

export function useReportGroupMutations() {
  const [, executeCreate] = useMutation(CreateReportGroupMutation);
  const [, executeUpdate] = useMutation(UpdateReportGroupMutation);
  const [, executeGenerate] = useMutation(GenerateReportGroupReportsMutation);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const createReportGroup = useCallback(
    async (input: CreateReportGroupInput): Promise<SaveReportGroupResult> => {
      setLoading(true);
      setError(null);
      try {
        const response = await executeCreate({ input });
        if (response.error)
          throw new Error(
            toUserFacingMessage(response.error, "保存に失敗しました"),
          );
        return {
          succeeded: Boolean(response.data?.createReportGroup),
          errorMessage: null,
        };
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : "レポートの作成に失敗しました";
        setError(errorMessage);
        return { succeeded: false, errorMessage };
      } finally {
        setLoading(false);
      }
    },
    [executeCreate],
  );

  const updateReportGroup = useCallback(
    async (input: UpdateReportGroupInput): Promise<SaveReportGroupResult> => {
      setLoading(true);
      setError(null);
      try {
        const response = await executeUpdate({ input });
        if (response.error)
          throw new Error(
            toUserFacingMessage(response.error, "保存に失敗しました"),
          );
        return {
          succeeded: Boolean(response.data?.updateReportGroup),
          errorMessage: null,
        };
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : "レポートの更新に失敗しました";
        setError(errorMessage);
        return { succeeded: false, errorMessage };
      } finally {
        setLoading(false);
      }
    },
    [executeUpdate],
  );

  const generateReportGroupReports = useCallback(
    async (reportGroupId: string): Promise<GenerateReportsResult> => {
      setLoading(true);
      setError(null);
      try {
        const response = await executeGenerate({ reportGroupId });
        if (response.error)
          throw new Error(
            toUserFacingMessage(response.error, "作成に失敗しました"),
          );
        return {
          createdCount: response.data?.generateReportGroupReports ?? 0,
          errorMessage: null,
        };
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : "レポートの作成に失敗しました";
        setError(errorMessage);
        return { createdCount: null, errorMessage };
      } finally {
        setLoading(false);
      }
    },
    [executeGenerate],
  );

  return {
    createReportGroup,
    updateReportGroup,
    generateReportGroupReports,
    loading,
    error,
  };
}
